import { LogOut, KeyRound, ChevronRight } from "lucide-react"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { useMemo } from "react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { IconWithLoading } from "@/components/icons"
import { useDefaultLocation } from "@/hooks/useDefaultLocation"
import { useProfileData } from "@/hooks/useProfile"
import { useAuthSignOut } from "@/modules/auth/service/auth"
import { storage } from "@/lib/storage"
import { NavigationSidebarSection } from "@/constants/navigation"
import { useTranslations } from "@/hooks/useTranslations"
import { BrandLogo } from '@/components/common/BrandLogo'
import { cn } from "@/lib/utils"
import { Footer } from "./common"
import { canShowAdminSidebarItem } from "@/utils/sidebarNavVisibility"
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES } from "@/constants/bill-location"
import { toast } from "@/components/common/Toast"
import type { BillLocationValue } from "@/constants/bill-location"

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  items: NavigationSidebarSection[]
  onChangePassword?: () => void
}

export function AppSidebar({ items, onChangePassword, className, ...props }: AppSidebarProps) {
  const { profile, checkPermission, isLoading: isLoadingProfile } = useProfileData()
  const { defaultLocation, isSaving, setDefaultLocation } = useDefaultLocation()
  const { mutateAsync: signOut } = useAuthSignOut()
  const navigate = useNavigate()
  const router = useRouterState()
  const pathname = router.location.pathname
  const t = useTranslations('sidebar')
  const { state, setOpenMobile, isMobile, toggleSidebar } = useSidebar()

  const handleLogout = async () => {
    const { error } = await signOut()
    if (!error) {
      storage.remove('accessToken')
      storage.remove('refreshToken')
      storage.remove('userRole')
      navigate({ to: '/auth/login' })
    }
  }

  const handleNavigation = (href: string) => {
    navigate({ to: href })
    // Close mobile sidebar after navigation
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const isActiveItem = (activePaths: string[]) => {
    return activePaths.some((pathPattern) => {
      const regex = new RegExp(pathPattern === '' ? '^$|^\/$' : pathPattern)
      return regex.test(pathname)
    })
  }

  const userInitials = useMemo(() => {
    if (!profile?.firstname || !profile?.lastname) return 'U'
    return `${profile.firstname[0]}${profile.lastname[0]}`.toUpperCase()
  }, [profile])

  const handleDefaultLocationChange = async (value: string) => {
    const next = value === 'all' ? '' : (value as BillLocationValue)
    try {
      await setDefaultLocation(next)
      toast.toastSuccess('บันทึกสถานที่เริ่มต้นแล้ว')
    } catch {
      toast.toastFailed('บันทึกสถานที่ไม่สำเร็จ')
    }
  }

  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className={cn(
        "relative overflow-visible rounded-tr-[40px] rounded-br-[40px] border-r border-sidebar-border bg-sidebar",
        className,
      )}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-tr-[40px] rounded-br-[40px]">
      <SidebarHeader className="bg-sidebar px-2 py-2 group-data-[state=expanded]:py-4 group-data-[state=expanded]:px-4 ">
        <Link to="/admin" className="flex flex-col gap-2 group-data-[state=expanded]:gap-6">
          <div className="flex items-start justify-start w-full transition-all duration-300">
            <BrandLogo className="h-[72px] w-auto max-w-[260px] shrink-0 group-data-[state=expanded]:h-[100px] group-data-[state=expanded]:max-h-[120px] group-data-[state=expanded]:max-w-[min(100%,260px)]" />
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="scroll-py-2 bg-[#FFF0D6]">
        {items.map((section) => (
          <div key={section.id} className="px-2 py-4">
            {section.label && !section.hideLabel && state === "expanded" && (
              <div className="px-3 mb-3">
                <h2 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">
                  {t(section.label)}
                </h2>
              </div>
            )}
            <SidebarMenu>
              {section.items
                .filter((item) => canShowAdminSidebarItem(item.id, checkPermission, profile?.role))
                .map((item) => {
                  const isActive = isActiveItem(item.activePaths)
                    return (
                      <SidebarMenuItem key={item.id} className="relative flex justify-center">
                        <SidebarMenuButton
                          isActive={isActive}
                          onClick={() => handleNavigation(item.href)}
                          tooltip={state === "collapsed" ? t(item.label) : undefined}
                          className={cn(
                            "group/item py-6 transition-all duration-200 rounded-xl mx-2 w-[calc(100%-16px)]",
                            "group-data-[state=collapsed]:w-10 group-data-[state=collapsed]:h-10 group-data-[state=collapsed]:py-0 group-data-[state=collapsed]:mx-0 group-data-[state=collapsed]:flex group-data-[state=collapsed]:items-center group-data-[state=collapsed]:justify-center",
                            "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-primary",
                            "data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-semibold data-[active=true]:shadow-md data-[active=true]:shadow-primary/20",
                            isActive && state === "expanded" && "sidebar-active-indicator"
                          )}
                        >
                          <IconWithLoading
                            icon={item.icon}
                            className={cn(
                              "h-5 w-5 transition-colors duration-200 shrink-0",
                              isActive && state === "expanded" ? "text-sidebar-primary-foreground" : "",
                              isActive && state === "collapsed" ? "text-sidebar-primary-foreground" : "group-hover/item:text-sidebar-primary"
                            )}
                          />
                          {state === "expanded" && <span className="text-sm font-medium truncate">{t(item.label)}</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                })}
            </SidebarMenu>
          </div>
        ))}
        
        {/* Quick Scan button removed per request */}
      </SidebarContent>

      <SidebarFooter className="bg-sidebar p-2">
      {state === "expanded" && (
            <div className="flex items-center gap-3 px-1 mt-2 transition-all duration-300 animate-in fade-in zoom-in-95">
              <div className="relative">
                <Avatar className="h-10 w-10 border border-sidebar-border bg-sidebar-accent">
                  <AvatarFallback className="bg-primary text-primary-foreground font-bold text-xs">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-s-brand-secondary border-2 border-white" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-bold text-gray-900 text-sm truncate">
                  {isLoadingProfile ? 'กำลังโหลด...' : `${profile?.firstname || ''} ${profile?.lastname || ''}`}
                </span>
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest truncate">
                  {profile?.role?.name || 'ผู้ใช้งาน'}
                </span>
              </div>
            </div>
          )}
        {state === "expanded" && (
          <div className="px-1 pb-2">
            <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-gray-400">
              สถานที่เริ่มต้น
            </span>
            <Select
              value={defaultLocation || 'all'}
              onValueChange={handleDefaultLocationChange}
              disabled={isLoadingProfile || isSaving}
            >
              <SelectTrigger className="h-9 rounded-xl bg-white/70">
                <SelectValue placeholder="ทุกสาขา" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">ทุกสาขา</SelectItem>
                {BILL_LOCATION_VALUES.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {BILL_LOCATION_LABELS[loc]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <SidebarMenu className="group-data-[state=collapsed]:gap-4">
          {checkPermission("admin_change_password") && onChangePassword && (
            <SidebarMenuItem className="flex justify-center">
              <SidebarMenuButton 
                onClick={onChangePassword}
                tooltip={state === "collapsed" ? "เปลี่ยนรหัสผ่าน" : undefined}
                className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-primary rounded-xl py-5 group-data-[state=collapsed]:w-10 group-data-[state=collapsed]:h-10 group-data-[state=collapsed]:py-0 items-center justify-start flex"
              >
                <KeyRound className="h-4 w-4 shrink-0" />
                {state === "expanded" && <span className="text-sm ml-2">เปลี่ยนรหัสผ่าน</span>}
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem className="flex justify-center">
            <SidebarMenuButton 
              onClick={handleLogout}
              tooltip={state === "collapsed" ? "ออกจากระบบ" : undefined}
              className="text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl py-5 font-semibold group-data-[state=collapsed]:w-10 group-data-[state=collapsed]:h-10 group-data-[state=collapsed]:py-0 items-center justify-start flex"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {state === "expanded" && <span className="text-sm ml-2">ออกจากระบบ</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {state !== "collapsed" && <div className="mt-2 pt-2 border-t border-sidebar-border">
          <Footer />
        </div>}
      </SidebarFooter>
      </div>

      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn(
          "absolute right-0 top-1/2 z-50 hidden size-8 -translate-y-1/2 translate-x-1/2",
          "rounded-full border border-sidebar-border bg-background shadow-md hover:bg-muted md:inline-flex",
        )}
        onClick={toggleSidebar}
        aria-label={state === "expanded" ? "ย่อเมนู" : "ขยายเมนู"}
      >
        <ChevronRight
          className={cn(
            "size-4 transition-transform duration-200",
            state === "expanded" && "rotate-180",
          )}
        />
      </Button>
      <SidebarRail />
    </Sidebar>
  )
}
